/**
 * Snapshot Formatter Search Helpers
 *
 * Search, relevance scoping, and context enrichment for semantic snapshots.
 *
 * @module
 */

import type { SnapshotElement } from './snapshot-analyzer.js';
import { gridContainsRef } from './snapshot-formatter-grid.js';
import {
  MAX_SEARCH_MATCHES,
  SECTION_BOUNDARY_ROLES,
  getDisplayText,
  isSearchInteractiveElement,
  normalizeSearchText,
  type SearchMatch,
  type SearchResult,
  type Section,
} from './snapshot-formatter-shared.js';

export function findRelevanceScope(
  elements: SnapshotElement[],
  activeElementRef: string | undefined
): string | null {
  if (!activeElementRef) return null;

  const path: Array<{ ref: string; role: string }> = [];

  function findPath(el: SnapshotElement): boolean {
    if (el.ref) {
      path.push({ ref: el.ref, role: el.role });
    }

    if (el.ref === activeElementRef) {
      return true;
    }

    for (const child of el.children) {
      if (findPath(child)) return true;
    }

    if (el.ref) path.pop();
    return false;
  }

  for (const el of elements) {
    if (findPath(el)) break;
  }

  for (let i = path.length - 1; i >= 0; i--) {
    if (SECTION_BOUNDARY_ROLES.has(path[i].role)) {
      return path[i].ref;
    }
  }

  return null;
}

export function searchElements(
  elements: SnapshotElement[],
  searchTerms: string[],
  scopeRef: string | null = null
): SearchResult {
  const allMatches: SearchMatch[] = [];
  const normalizedTerms = searchTerms.map((t) => normalizeSearchText(t)).filter(Boolean);

  if (normalizedTerms.length === 0) return { matches: [], totalFound: 0 };

  function searchElement(el: SnapshotElement, inScope: boolean): void {
    const nowInScope = inScope || el.ref === scopeRef || scopeRef === null;

    if (el.ref && nowInScope) {
      const searchLabel = getDisplayText(el);
      const searchText = [searchLabel, el.inputValue].filter(Boolean).join(' ');
      const normalizedSearchText = normalizeSearchText(searchText);

      for (const term of normalizedTerms) {
        if (normalizedSearchText.includes(term)) {
          allMatches.push({
            ref: el.ref,
            role: el.role,
            label: searchLabel || el.role,
            term,
            isInteractive: isSearchInteractiveElement(el),
          });
          break;
        }
      }
    }

    for (const child of el.children) {
      searchElement(child, nowInScope);
    }
  }

  for (const el of elements) {
    searchElement(el, scopeRef === null);
  }

  const totalFound = allMatches.length;
  allMatches.sort((a, b) => {
    if (a.isInteractive && !b.isInteractive) return -1;
    if (!a.isInteractive && b.isInteractive) return 1;
    return 0;
  });

  return {
    matches: allMatches.slice(0, MAX_SEARCH_MATCHES),
    totalFound,
  };
}

interface SectionSearchResult {
  section: Section;
  ancestors: Section[];
  path: string[];
}

function findElementInSections(
  sections: Section[],
  targetRef: string,
  ancestors: Section[] = []
): SectionSearchResult | null {
  for (const section of sections) {
    const newAncestors = [...ancestors, section];
    const path = newAncestors.map((s) => s.heading);

    if (section.elements.some((el) => el.ref === targetRef)) {
      return { section, ancestors: newAncestors, path };
    }

    if (section.gridInfo && gridContainsRef(section.gridInfo, targetRef)) {
      return { section, ancestors: newAncestors, path };
    }

    const found = findElementInSections(section.subsections, targetRef, newAncestors);
    if (found) return found;
  }
  return null;
}

export function populateSearchContext(matches: SearchMatch[], sections: Section[]): void {
  for (const match of matches) {
    const result = findElementInSections(sections, match.ref);
    if (result) {
      const { section, ancestors, path } = result;
      const inFocusedDialog = ancestors.some((s) => s.isFocused);
      const inBackground = section.isBackground || ancestors.some((s) => s.isBackground);

      if (inFocusedDialog) {
        match.context = 'focused';
      } else if (inBackground) {
        match.context = 'background';
      } else {
        match.context = 'foreground';
      }

      const SKIP_HEADINGS = ['generic', 'main', 'iframe'];
      match.sectionPath = path
        .filter((p) => !SKIP_HEADINGS.includes(p.toLowerCase()))
        .slice(-3)
        .join(' > ');
    }
  }
}

export function sortMatchesByContext(matches: SearchMatch[]): SearchMatch[] {
  const contextOrder: Record<string, number> = {
    focused: 0,
    foreground: 1,
    background: 2,
  };

  return [...matches].sort((a, b) => {
    const aContext = contextOrder[a.context ?? 'foreground'] ?? 1;
    const bContext = contextOrder[b.context ?? 'foreground'] ?? 1;
    if (aContext !== bContext) return aContext - bContext;

    if (a.isInteractive && !b.isInteractive) return -1;
    if (!a.isInteractive && b.isInteractive) return 1;

    return 0;
  });
}
